import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLaunchBoardroom } from "@/lib/onboarding";
import HeroBoardroomMock from "./HeroBoardroomMock";

export default function HeroSection() {
  const launch = useLaunchBoardroom();

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden px-5 pb-24 pt-32 md:px-8 md:pt-40"
    >
      {/* Soft gradient blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -z-10 h-[600px] w-[1100px] -translate-x-1/2"
      >
        <div className="absolute left-0 top-12 h-[300px] w-[400px] rounded-full bg-violet-300/30 blur-3xl" />
        <div className="absolute right-12 top-0 h-[280px] w-[420px] rounded-full bg-cyan-200/40 blur-3xl" />
        <div className="absolute left-1/3 top-32 h-[280px] w-[380px] rounded-full bg-amber-200/35 blur-3xl" />
      </div>

      <div className="mx-auto max-w-6xl">
        {/* Eyebrow pill */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex justify-center"
        >
          <span
            data-testid="hero-eyebrow-pill"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/[0.06] bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-700 backdrop-blur"
          >
            <Sparkles className="h-3 w-3 text-violet-600" strokeWidth={2.4} />
            Now in private beta
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="text-center font-black leading-[0.92] tracking-[-0.04em] text-zinc-900"
          style={{ fontWeight: 900 }}
        >
          <span className="block text-[44px] sm:text-[80px] md:text-[112px] xl:text-[136px]">
            Run your company
          </span>
          <span className="block text-[44px] sm:text-[80px] md:text-[112px] xl:text-[136px]">
            like a Fortune 500.
          </span>
          <span className="block text-[44px] text-zinc-300 sm:text-[80px] md:text-[112px] xl:text-[136px]">
            Without the headcount.
          </span>
        </motion.h1>

        {/* Subhead */}
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.15 }}
          className="mx-auto mt-8 max-w-2xl text-center text-[16px] leading-relaxed text-zinc-600 md:text-[19px]"
        >
          Idea AI gives solo founders an executive boardroom of eight specialized
          AI agents — CTO, CFO, CMO, COO, Product, Sales, EA, Admin. They debate.
          You decide. Ship faster.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.22 }}
          className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <button
            type="button"
            onClick={launch}
            data-testid="hero-cta-primary"
            className="group inline-flex h-12 items-center gap-2 rounded-full bg-black px-6 text-[14px] font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-zinc-800 hover:shadow-[0_18px_36px_-18px_rgba(0,0,0,0.55)]"
          >
            Launch boardroom
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.4}
            />
          </button>
        </motion.div>

        <div
          data-testid="hero-free-access-caption"
          className="mt-4 text-center text-[13px] text-zinc-500"
        >
          Free Access Until 3 May 2026
        </div>

        <div className="mt-16">
          <HeroBoardroomMock />
        </div>
      </div>
    </section>
  );
}
